import { IGatsbyImageData } from "gatsby-plugin-image";
import { isGatsbyImageData } from "helpers/is-gatsby-image";
import { useGenerateSiteLink } from "hooks/use-generate-site-link";
import { getPlaceholderImage } from "hooks/get-placeholder-image";

type ExternalLinkData = Queries.ExternalLinkFragment;
type InternalPageLinkData = Queries.InternalPageLinkFragment;
type InternalItemLinkData = Queries.InternalItemLinkFragment;

type DatoLinkData =
    | ExternalLinkData
    | InternalPageLinkData
    | InternalItemLinkData
    | null
    | undefined;

// Image Card with Label. If true and certain type event, get start and end date
type LinkData = {
    id: string;
    __typename: string;
    url: string;
    target: string;
    label?: string;
    title?: string;
    description?: string;
    shortDescription?: string;
    image?: IGatsbyImageData;
    carouselCardImage?: IGatsbyImageData;
    startDate?: string;
    endDate?: string;
    signatureEventId?: string;
};

export function getLinkDataFromDato(link: DatoLinkData): LinkData | null {
    if (!link) return null;

    if (link.__typename === "DatoCmsExternalLink") {
        return {
            id: link.id,
            __typename: link.__typename,
            label: link.linkLabel ?? "",
            url: link.url ?? "",
            target: "_blank", // external links should open in a new tab
            title: link.linkLabel ?? undefined // external link cards will use the link label as their title
        };
    } else if (link.__typename === "DatoCmsInternalPageLink" && link.link) {
        let linkUrlData = useGenerateSiteLink(link);

        let linkUrl = linkUrlData?.url ?? "#";

        let title = link.link.title ?? "Error: missing page title";

        if (link.link.__typename === "DatoCmsPage") {
            
            title = link.link.titleOverride || link.link.title || "Error: missing page title";

        }

        let description = link.link.seo?.description ?? "";
        let shortDescription = link.link.seo?.description ?? "";

        // check for description
        if (
            link.link.__typename === "DatoCmsEvent" ||
            link.link.__typename === "DatoCmsBuilding" ||
            link.link.__typename === "DatoCmsDeal" ||
            link.link.__typename === "DatoCmsPerformer" ||
            link.link.__typename === "DatoCmsRentalSpace" ||
            link.link.__typename === "DatoCmsService"
        ) {
            // if empty fallback to seo description
            description = link.link.description ?? description;
            shortDescription = link.link.shortDescription ?? shortDescription;
        }

        // get hero image and carousel card image
        let image = undefined;
        let carouselCardImage = undefined;

        if (
            link.link.__typename === "DatoCmsBuilding" ||
            link.link.__typename === "DatoCmsEvent" ||
            link.link.__typename === "DatoCmsPage" ||
            link.link.__typename === "DatoCmsDeal" ||
            link.link.__typename === "DatoCmsArea" ||
            link.link.__typename === "DatoCmsPerformer" ||
            link.link.__typename === "DatoCmsRentalSpace" ||
            link.link.__typename === "DatoCmsService" ||
            link.link.__typename === "DatoCmsUniversalTag"
        ) {
            image = isGatsbyImageData(link.link.heroImage?.gatsbyImageData)
                ? link.link.heroImage.gatsbyImageData
                : undefined;
            carouselCardImage = isGatsbyImageData(link.link.heroImage?.carouselCardGatsbyImage)
                ? link.link.heroImage.carouselCardGatsbyImage
                : undefined;
        }

        // check for start and end date for Event
        let startDate = undefined;
        let endDate = undefined;

        if (
            link.link.__typename === "DatoCmsEvent" &&
            link.link.startDatetime &&
            link.link.endDatetime
        ) {
            startDate = link.link.startDatetime;
            endDate = link.link.endDatetime;
        }

        // if image is missing, use placeholder image
        if (image === undefined) {
            image = getPlaceholderImage();
        }

        // if carousel card image is missing, use placeholder image
        if (carouselCardImage === undefined) {
            carouselCardImage = getPlaceholderImage();
        }

        let linkData: LinkData = {
            id: link.link.id,
            __typename: link.link.__typename,
            label: link.linkLabel ?? "Error: missing page title",
            url: linkUrl,
            target: "_self",
            title: title,
            description: description,
            shortDescription: shortDescription,
            image: image,
            carouselCardImage: carouselCardImage,
            startDate: startDate,
            endDate: endDate,
        };

        return linkData;
    } else if (link.__typename === "DatoCmsInternalItemLink" && link.link) {
        let linkUrlData = useGenerateSiteLink(link);

        let linkUrl = linkUrlData?.url ?? "#";

        let title = link.link.title ?? "Error: missing page title";

        let description = "";
        let shortDescription = "";

        // check for seo description
        if (
            link.link.__typename === "DatoCmsActivity" ||
            link.link.__typename === "DatoCmsAnimalExhibit" ||
            link.link.__typename === "DatoCmsVendor"
        ) {
            description = link.link.seo?.description ?? "";
            shortDescription = link.link.seo?.description ?? "";
        }

        // check for description
        if (
            link.link.__typename === "DatoCmsActivity" ||
            link.link.__typename === "DatoCmsAnimalExhibit" ||
            link.link.__typename === "DatoCmsRide" ||
            link.link.__typename === "DatoCmsVendor"
        ) {
            // if empty fallback to seo description
            description = link.link.description ?? description;
            shortDescription = link.link.shortDescription ?? shortDescription;
        }

        // get hero image and carousel card image
        let image = undefined;
        let carouselCardImage = undefined;

        if (
            link.link.__typename === "DatoCmsActivity" ||
            link.link.__typename === "DatoCmsAnimalExhibit" ||
            link.link.__typename === "DatoCmsRide" ||
            link.link.__typename === "DatoCmsVendor"
        ) {
            image = isGatsbyImageData(link.link.heroImage?.gatsbyImageData)
                ? link.link.heroImage.gatsbyImageData
                : undefined;
            carouselCardImage = isGatsbyImageData(link.link.heroImage?.carouselCardGatsbyImage)
                ? link.link.heroImage.carouselCardGatsbyImage
                : undefined;
        }

        // if image is missing, use placeholder image
        if (image === undefined) {
            image = getPlaceholderImage();
        }

        // if carousel card image is missing, use placeholder image
        if (carouselCardImage === undefined) {
            carouselCardImage = getPlaceholderImage();
        }

        let linkData: LinkData = {
            id: link.link.id,
            __typename: link.link.__typename,
            label: link.linkLabel ?? "Error: missing page title",
            url: linkUrl,
            target: "_self",
            title: title,
            description: description,
            shortDescription: shortDescription,
            image: image,
            carouselCardImage: carouselCardImage,
        };

        return linkData;
    }

    let linkData: LinkData = {
        id: "error",
        __typename: "error",
        label: "Error: missing link data",
        url: "#",
        target: "_self",
    };

    return linkData;
}
